import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Chip } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { ReactComponent as DropdownArrow } from '../../assets/DropdownArrow.svg';

const MultiSelect = ({
  helperText,
  required = false,
  renderOption,
  handleBlur,
  limitTags = 3,
  getTagChipLabel,
  getOptionLabel,
  ...props
}) => {
  const selectAll = {
    label: 'Select all options',
    value: 'mui_auto_complete_select_all',
  };
  const handleChange = useCallback(
    (_e, value) => {
      if (value.includes(selectAll)) {
        if (props?.disabledOptions?.length) {
          return props?.onChange?.(
            props?.options?.filter(
              (ele) => !props?.disabledOptions?.includes(ele.value)
            )
          );
        }
        return props?.onChange(props.options);
      }
      return props?.onChange(value);
    },
    [props]
  );

  const options = useMemo(() => {
    if (getOptionLabel || renderOption) {
      return props?.options;
    }
    if (props?.options?.length) {
      return [selectAll, ...props.options];
    }
    return [];
  }, [props.options, getOptionLabel, renderOption]);

  return (
    <Autocomplete
      disableCloseOnSelect
      size="small"
      popupIcon={<DropdownArrow />}
      isOptionEqualToValue={(item, current) => item.value === current.value}
      sx={(theme) => ({
        '& .MuiInputBase-root.Mui-disabled': {
          background: `${theme.palette.other.white2} !important`,
        },
        '& .MuiAutocomplete-popupIndicator': {
          color: theme.palette.primary.main,
        },
      })}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { onDelete, ...chipProps } = getTagProps({ index });
          return option.value !== 'mui_auto_complete_select_all' ? (
            <Chip
              color={props.color ? props.color : 'primary'}
              size={props?.size ? props?.size : 'small'}
              label={getTagChipLabel ? getTagChipLabel(option) : option.label}
              onDelete={() => {
                onDelete(chipProps.key);
              }}
              {...chipProps}
            />
          ) : null;
        })
      }
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          name={props.name}
          required={required}
          label={props.label}
          placeholder={!props?.value?.length ? props?.placeholder : ''}
          error={Boolean(props.error)}
          helperText={helperText}
          onBlur={handleBlur}
          InputLabelProps={{
            shrink: true,
          }}
        />
      )}
      {...props}
      getOptionLabel={getOptionLabel || ((option) => option.label)}
      multiple
      limitTags={limitTags}
      options={options}
      onChange={handleChange}
    />
  );
};

MultiSelect.propTypes = {
  helperText: PropTypes.string.isRequired,
  required: PropTypes.bool,
  renderOption: PropTypes.func,
  handleBlur: PropTypes.func,
  limitTags: PropTypes.number,
  getTagChipLabel: PropTypes.func,
  getOptionLabel: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  disabledOptions: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  color: PropTypes.string,
  size: PropTypes.string,
};

MultiSelect.defaultProps = {
  required: false,
  renderOption: null,
  handleBlur: null,
  limitTags: 3,
  getTagChipLabel: null,
  getOptionLabel: null,
  name: '',
  label: '',
  placeholder: '',
  error: false,
  value: [],
  options: [],
  disabledOptions: [],
  onChange: () => {},
  color: 'primary',
  size: 'small',
};

export default MultiSelect;
